import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Label,
  Switch,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@ais-ui/ui-core';
import { UseFormReturn } from 'react-hook-form';

import { ShapeSelect } from '../../../Shapes/ShapeSelects/ShapeSelect';

import { ReportShapeItem, ShapeStructure, ShapeType } from '../../reportsTypes';
import { ShapeMultiSelect } from '../../../Shapes/ShapeSelects/ShapeMultiSelect';
import { ReportCloneValues } from '../utils/createReportCloneFormSchema';
import React, { useEffect, useMemo, useState } from 'react';
import { PortMultiSelect } from '../../../Shapes/ShapeSelects/PortMultiSelect';
import { useLocalization } from '@ais-ui/locale-management';
import { DrawPolygonField } from '../../../Shapes/DrawFormFields/DrawPolygonField';
import { DrawLineField } from '../../../Shapes/DrawFormFields/DrawLineField';
import { DrawPointField } from '../../../Shapes/DrawFormFields/DrawPointField';
import { DrawCircleField } from '../../../Shapes/DrawFormFields/DrawCircleField';
import { useMutateReportContext } from '../CreateReportTemplate/CreateReportContext';
import { FormFieldTypes } from '../utils/formFieldTypes';
import { useDrawWithViewMode } from '../../../../hooks/useDrawWithViewMode';

interface ShapeStepProps {
  form: UseFormReturn<ReportCloneValues>;
  shapeType: ShapeType;
  setShapeType: (shapeType: ShapeType) => void;
  shapeFields: ShapeStructure;
}

interface AdHocShapeSelectProps {
  allowedGeometryTypes: string[];
  geometryField: string;
  form: UseFormReturn<any>;
  shouldAutoTrigger: boolean;
  onAutoTriggerComplete: () => void;
}

const AdHocShapeSelect = ({
  allowedGeometryTypes,
  geometryField,
  form,
  shouldAutoTrigger,
  onAutoTriggerComplete,
}: AdHocShapeSelectProps) => {
  const [tab, setTab] = useState<string>('polygon');
  const [maxPoints, setMaxPoints] = useState<number | undefined>();
  const { setShowDialog, instantShapeData } = useMutateReportContext();

  const { onStartDrawingFromDialog } = useDrawWithViewMode(
    form,
    setShowDialog,
    geometryField
  );

  const lowerCaseTypes = allowedGeometryTypes.map((type) => type.toLowerCase());

  useEffect(() => {
    if (lowerCaseTypes.length <= 1) {
      if (lowerCaseTypes.includes('polygon')) {
        setTab('polygon');
        setMaxPoints(undefined);
      } else if (lowerCaseTypes.includes('linestring')) {
        setTab('linestring');
        setMaxPoints(undefined);
      } else if (lowerCaseTypes.includes('twopointlinestring')) {
        setTab('linestring');
        setMaxPoints(2);
      } else if (lowerCaseTypes.includes('point')) {
        setTab('point');
        setMaxPoints(undefined);
      } else if (lowerCaseTypes.includes('circle')) {
        setTab('circle');
        setMaxPoints(undefined);
      }
    }
  }, [lowerCaseTypes]);

  // Watch the form field value to get reactive updates
  const formGeometry = form.watch(geometryField);

  // Use instantShapeData as fallback if form doesn't have the geometry yet
  const geometry = formGeometry || instantShapeData?.[geometryField];

  // Auto-trigger drawing when shouldAutoTrigger is true
  useEffect(() => {
    if (shouldAutoTrigger && !geometry) {
      // Trigger drawing based on the current tab
      if (tab === 'polygon') {
        onStartDrawingFromDialog('Polygon', null);
      } else if (tab === 'linestring') {
        onStartDrawingFromDialog('LineString', null);
      } else if (tab === 'point') {
        onStartDrawingFromDialog('Point', null);
      } else if (tab === 'circle') {
        onStartDrawingFromDialog('Circle', null);
      }
      onAutoTriggerComplete();
    }
  }, [
    shouldAutoTrigger,
    geometry,
    tab,
    onStartDrawingFromDialog,
    onAutoTriggerComplete,
  ]);

  return (
    <Tabs
      value={tab}
      onValueChange={setTab}
      defaultValue="polygon"
      className="w-full space-y-4 pt-4 h-full flex flex-col"
    >
      {lowerCaseTypes.length > 1 ? (
        <TabsList className="w-fit">
          {lowerCaseTypes.includes('polygon') && (
            <TabsTrigger value="polygon">Polygon</TabsTrigger>
          )}
          {lowerCaseTypes.includes('linestring') && (
            <TabsTrigger value="linestring">Line</TabsTrigger>
          )}
          {lowerCaseTypes.includes('point') && (
            <TabsTrigger value="point">Point</TabsTrigger>
          )}
          {lowerCaseTypes.includes('circle') && (
            <TabsTrigger value="circle">Circle</TabsTrigger>
          )}
        </TabsList>
      ) : null}
      {tab === 'polygon' && (
        <DrawPolygonField
          geometry={geometry}
          onStartDrawing={onStartDrawingFromDialog}
          color={'blue'}
        ></DrawPolygonField>
      )}
      {tab === 'linestring' && (
        <DrawLineField
          geometry={geometry}
          onStartDrawing={onStartDrawingFromDialog}
          maxPoints={maxPoints}
          color={'blue'}
        ></DrawLineField>
      )}
      {tab === 'point' && (
        <DrawPointField
          geometry={geometry}
          onStartDrawing={onStartDrawingFromDialog}
          color={'blue'}
        ></DrawPointField>
      )}
      {tab === 'circle' && (
        <DrawCircleField
          geometry={geometry}
          onStartDrawing={onStartDrawingFromDialog}
          color={'blue'}
        ></DrawCircleField>
      )}
    </Tabs>
  );
};

interface ShapeFormProps {
  form: UseFormReturn<ReportCloneValues>;
  shapeItems: ReportShapeItem[];
}

interface SingleContentProps {
  form: UseFormReturn<ReportCloneValues>;
  shapeFields: ShapeStructure;
}

interface TabbedContentProps {
  form: UseFormReturn<ReportCloneValues>;
  shapeFields: ShapeStructure;
  shapeType: ShapeType;
  setShapeType: (shapeType: ShapeType) => void;
}

const ShapeForm = ({ form, shapeItems }: ShapeFormProps) => {
  const { messages } = useLocalization();
  const { instantShape, setInstantShape, instantShapeData } =
    useMutateReportContext();
  const previousInstantShapeRef = React.useRef(instantShape);
  const [shouldAutoTrigger, setShouldAutoTrigger] = React.useState(false);

  // Track when user explicitly toggles instant shape to true
  React.useEffect(() => {
    if (!previousInstantShapeRef.current && instantShape) {
      // Changed from false to true - user clicked the toggle
      setShouldAutoTrigger(true);
      // Clear validation errors when switching to instant shape mode
      shapeItems.forEach((shapeItem) => {
        form.clearErrors(shapeItem.name);
      });
    } else if (previousInstantShapeRef.current && !instantShape) {
      // Changed from true to false - clear geometry fields
      shapeItems.forEach((shapeItem) => {
        form.setValue(shapeItem.name, '', { shouldValidate: false });
        form.clearErrors(shapeItem.name);
      });
    }
    previousInstantShapeRef.current = instantShape;
  }, [instantShape, shapeItems, form]);

  // Sync instantShapeData to form when it changes (when user draws a shape)
  React.useEffect(() => {
    if (!instantShape || !instantShapeData) {
      return;
    }

    // Update form fields with instant shape data
    shapeItems.forEach((shapeItem) => {
      const geometry = instantShapeData[shapeItem.name];
      if (geometry) {
        form.setValue(shapeItem.name, geometry, { shouldValidate: true });
      }
    });
  }, [instantShapeData, instantShape, shapeItems, form]);

  return (
    <>
      {shapeItems.map((shapeItem) => (
        <FormField
          key={shapeItem.name}
          control={form.control}
          name={shapeItem.name}
          render={({ field }) => (
            <FormItem>
              <div className="flex justify-between items-center mb-2">
                <FormLabel>{shapeItem.label}: *</FormLabel>
                <div className="flex items-center space-x-2">
                  <Switch
                    id="report-shape-switch"
                    checked={instantShape}
                    onCheckedChange={(value) => {
                      setInstantShape(value);
                    }}
                  />
                  <Label className="text-sm">
                    {messages.reportsCreateReportShapeInstantShape}
                  </Label>
                  <InfoIconTooltip>
                    {messages.reportsCreateReportShapeInstantShapeTooltip}
                  </InfoIconTooltip>
                </div>
              </div>
              <FormControl onChange={() => form.clearErrors()}>
                {!instantShape ? (
                  <ShapeSelect
                    id="shape-select-dropdown"
                    value={FormFieldTypes.asString(field)}
                    onChange={field.onChange}
                    allowedGeometryTypes={shapeItem.allowedGeometryTypes}
                  ></ShapeSelect>
                ) : (
                  <AdHocShapeSelect
                    allowedGeometryTypes={shapeItem.allowedGeometryTypes}
                    geometryField={shapeItem.name}
                    form={form}
                    shouldAutoTrigger={shouldAutoTrigger}
                    onAutoTriggerComplete={() => setShouldAutoTrigger(false)}
                  />
                )}
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ))}
    </>
  );
};

const MultiShapeForm = ({ form, shapeItems }: ShapeFormProps) => {
  return (
    <>
      {shapeItems.map((shapeItem) => (
        <FormField
          key={shapeItem.name}
          control={form.control}
          name={shapeItem.name}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{shapeItem.label}: *</FormLabel>
              <FormControl onChange={() => form.clearErrors()}>
                <ShapeMultiSelect
                  value={FormFieldTypes.asStringArray(field)}
                  onChange={field.onChange}
                  allowedGeometryTypes={shapeItem.allowedGeometryTypes}
                ></ShapeMultiSelect>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ))}
    </>
  );
};

const PortForm = ({ form, shapeItems }: ShapeFormProps) => {
  return (
    <>
      {shapeItems.map((shapeItem) => (
        <FormField
          key={shapeItem.name}
          control={form.control}
          name={shapeItem.name}
          render={({ field }) => (
            <FormItem>
              <FormLabel>{shapeItem.label}: *</FormLabel>
              <FormControl onChange={() => form.clearErrors()}>
                <PortMultiSelect
                  value={Array.isArray(field.value) ? field.value : []}
                  onChange={field.onChange}
                ></PortMultiSelect>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ))}
    </>
  );
};

const WorldForm = ({ form, shapeItems }: ShapeFormProps) => {
  return (
    <>
      {shapeItems.map((shapeItem) => (
        <FormField
          key={shapeItem.name}
          control={form.control}
          name={shapeItem.name}
          render={({ field }) => (
            <FormItem className="flex flex-col space-y-4">
              <FormLabel>{shapeItem.label}: *</FormLabel>
              <FormControl onChange={() => form.clearErrors()}>
                <Switch
                  disabled={true}
                  checked={FormFieldTypes.asBoolean(field)}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      ))}
    </>
  );
};

const SingleContent = ({ form, shapeFields }: SingleContentProps) => {
  if (
    Object.prototype.hasOwnProperty.call(shapeFields, 'shape') &&
    shapeFields.shape !== undefined
  ) {
    const shapeItems: ReportShapeItem[] = shapeFields.shape;
    return <ShapeForm form={form} shapeItems={shapeItems} />;
  } else if (
    Object.prototype.hasOwnProperty.call(shapeFields, 'multiShape') &&
    shapeFields.multiShape !== undefined
  ) {
    const shapeItems: ReportShapeItem[] = shapeFields.multiShape;
    return (
      <MultiShapeForm form={form} shapeItems={shapeItems}></MultiShapeForm>
    );
  } else if (
    Object.prototype.hasOwnProperty.call(shapeFields, 'worldShape') &&
    shapeFields.worldShape !== undefined
  ) {
    return;
  } else if (
    Object.prototype.hasOwnProperty.call(shapeFields, 'ports') &&
    shapeFields.ports !== undefined
  ) {
    const shapeItems: ReportShapeItem[] = shapeFields.ports;
    return <PortForm form={form} shapeItems={shapeItems}></PortForm>;
  }
};

const TabbedContent = ({
  form,
  shapeFields,
  shapeType,
  setShapeType,
}: TabbedContentProps) => {
  const shapeItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(shapeFields, 'shape')) {
      return shapeFields.shape;
    }
    return null; // or a default value
  }, [shapeFields]);

  const multiShapeItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(shapeFields, 'multiShape')) {
      return shapeFields.multiShape;
    }
    return null; // or a default value
  }, [shapeFields]);

  const portItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(shapeFields, 'ports')) {
      return shapeFields.ports;
    }
    return null; // or a default value
  }, [shapeFields]);

  const worldShapeItems = useMemo(() => {
    if (Object.prototype.hasOwnProperty.call(shapeFields, 'worldShape')) {
      return shapeFields.worldShape;
    }
    return null; // or a default value
  }, [shapeFields]);

  return (
    <Tabs
      value={shapeType}
      onValueChange={setShapeType as (value: string) => void}
      defaultValue="shape"
      className="w-full space-y-4 pt-4 h-full flex flex-col"
    >
      <TabsList className="w-fit">
        {shapeItems && <TabsTrigger value="shape">Shape</TabsTrigger>}
        {multiShapeItems && (
          <TabsTrigger value="multiShape">Multi shape</TabsTrigger>
        )}
        {portItems && <TabsTrigger value="ports">Port</TabsTrigger>}
        {worldShapeItems && <TabsTrigger value="worldShape">World</TabsTrigger>}
      </TabsList>
      {shapeItems && shapeType === 'shape' && (
        <ShapeForm form={form} shapeItems={shapeItems} />
      )}
      {multiShapeItems && shapeType === 'multiShape' && (
        <MultiShapeForm form={form} shapeItems={multiShapeItems} />
      )}
      {portItems && shapeType === 'ports' && (
        <PortForm form={form} shapeItems={portItems} />
      )}
      {worldShapeItems && shapeType === 'worldShape' && (
        <WorldForm form={form} shapeItems={worldShapeItems} />
      )}
    </Tabs>
  );
};

export const ShapeFormField = ({
  form,
  shapeFields,
  shapeType,
  setShapeType,
}: ShapeStepProps) => {
  return (
    <>
      {Object.keys(shapeFields).length === 1 && (
        <SingleContent form={form} shapeFields={shapeFields}></SingleContent>
      )}
      {Object.keys(shapeFields).length > 1 && (
        <TabbedContent
          form={form}
          shapeFields={shapeFields}
          shapeType={shapeType}
          setShapeType={setShapeType}
        ></TabbedContent>
      )}
    </>
  );
};
